"use client";

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { createClient } from "@/lib/supabase/client";
import type { Session } from "@supabase/supabase-js";
import type { User } from "@supabase/supabase-js";
import { getAuthSessionId } from "@/lib/auth/session-idle";
import { startSessionIdleMonitor } from "@/lib/auth/session-idle-monitor";

export type UserRole = "admin" | "scorer";

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create client once outside component
let supabaseClient: ReturnType<typeof createClient> | null = null;
let clientChecked = false;

function getSupabaseClient() {
  if (!clientChecked) {
    supabaseClient = createClient();
    clientChecked = true;
  }
  return supabaseClient;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const supabase = getSupabaseClient();
  
  const defaultValue = useMemo(() => ({
    user: null,
    profile: null,
    isLoading: false,
    isAdmin: false,
    signOut: async () => {},
    refreshProfile: async () => {},
  }), []);

  const loadProfile = useCallback(async (userId: string, signal?: AbortSignal) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single()
        .abortSignal(signal);
      
      if (!error && data) {
        setProfile(data);
      }
    } catch (err) {
      // Silently ignore abort errors
      if (err instanceof Error && (err.name === 'AbortError' || err.message.includes('aborted'))) {
        return;
      }
    }
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadProfile(user.id);
    }
  }, [user, loadProfile]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      window.location.href = "/auth/login";
    } catch (err) {
      // Silently ignore errors during signout
    }
  }, [supabase]);

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }
    
    const controller = new AbortController();
    let subscription: { unsubscribe: () => void } | null = null;
    
    // Get initial session with proper error handling for network failures
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (controller.signal.aborted) return;
        
        if (error) {
          console.warn("[v0] Supabase auth error:", error.message);
          setUser(null);
          setProfile(null);
          setIsLoading(false);
          return;
        }
        
        setUser(session?.user ?? null);
        setSessionId(session ? getAuthSessionId(session.access_token) : null);
        if (session?.user) {
          loadProfile(session.user.id, controller.signal);
        }
        setIsLoading(false);
      } catch (err) {
        // Handle network failures gracefully
        if (!controller.signal.aborted) {
          console.warn("[v0] Supabase auth network error:", err);
          setUser(null);
          setProfile(null);
          setIsLoading(false);
        }
      }
    };
    
    initAuth();

    // Listen for auth changes with error handling
    try {
      const { data } = supabase.auth.onAuthStateChange((_event: string, session: Session | null) => {
        if (controller.signal.aborted) return;
        
        setUser(session?.user ?? null);
        setSessionId(session ? getAuthSessionId(session.access_token) : null);
        if (session?.user) {
          loadProfile(session.user.id, controller.signal);
        } else {
          setProfile(null);
        }
      });
      subscription = data.subscription;
    } catch (err) {
      console.warn("[v0] Failed to setup auth listener:", err);
    }

    return () => {
      controller.abort();
      subscription?.unsubscribe();
    };
  }, [supabase, loadProfile]);

  useEffect(() => {
    if (!supabase || !user) return;
    const userId = user.id;
    return startSessionIdleMonitor({
      userId,
      onExpire: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        // A late response from an old monitor must never terminate a new login.
        if (session && (session.user.id !== userId || getAuthSessionId(session.access_token) !== sessionId)) return;
        try {
          await supabase.auth.signOut({ scope: "local" });
        } finally {
          setUser(null);
          setProfile(null);
          setSessionId(null);
          window.location.replace("/auth/login?reason=idle");
        }
      },
    });
  }, [supabase, user?.id, sessionId]);

  const value = useMemo(() => ({
    user,
    profile,
    isLoading,
    isAdmin: profile?.role === "admin",
    signOut,
    refreshProfile,
  }), [user, profile, isLoading, signOut, refreshProfile]);

  if (!supabase) {
    return <AuthContext.Provider value={defaultValue}>{children}</AuthContext.Provider>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return {
    ...context,
    userRole: context.profile?.role,
  };
}
